/**
 * How tall the AI badge renders over the asset it labels, in CSS pixels.
 *
 * A closed set rather than a free number for the same reason the corner is: the badge is a
 * compliance artifact, so brand can tune how much of a picture it takes up without a
 * deploy, but never shrink it to nothing or blow it up over the subject. The range was
 * picked against the live artwork — below 16px the pill's wording stops being readable, and
 * 48px is the size every badge rendered at before the setting existed.
 */
export type AiBadgeSize = 48 | 32 | 28 | 24 | 20 | 16;

/** Largest first, which is the order the admin picker lists them in. */
export const AI_BADGE_SIZES: readonly AiBadgeSize[] = [48, 32, 28, 24, 20, 16];

/**
 * The size an unset or unreadable config story resolves to.
 *
 * The size badges rendered at before the setting existed, so a space whose config story
 * has no such field keeps rendering what it rendered. Shrinking a compliance label is a
 * choice an editor makes per space on the labelling page, not a side effect of a deploy.
 */
export const DEFAULT_AI_BADGE_SIZE: AiBadgeSize = 48;

/** The `aiImagesConfig` field holding the site-wide size. */
export const AI_BADGE_SIZE_FIELD = "badge_size";

export function isAiBadgeSize(value: unknown): value is AiBadgeSize {
  return (
    typeof value === "number" &&
    (AI_BADGE_SIZES as readonly number[]).includes(value)
  );
}

/**
 * How each size is laid out, in CSS pixels — the one place a size's geometry is decided.
 *
 * `inset` is how far the badge is pinned from its corner; `compact` is the height of the
 * thumbnail badge under that site-wide size. 48px keeps the 28px inset and 32px thumbnail
 * badge it rendered with before the size became a setting, so a space that never picks a
 * size looks exactly as it did. The smaller sizes sit tighter in the corner, and their
 * thumbnail badge is the smallest size, so it is never larger than the full-size one.
 *
 * A size added to `AiBadgeSize` fails to compile until it has a row here, rather than
 * silently inheriting another size's layout.
 */
export const AI_BADGE_GEOMETRY: Record<
  AiBadgeSize,
  { inset: 12 | 28; compact: 32 | 16 }
> = {
  48: { inset: 28, compact: 32 },
  32: { inset: 12, compact: 16 },
  28: { inset: 12, compact: 16 },
  24: { inset: 12, compact: 16 },
  20: { inset: 12, compact: 16 },
  16: { inset: 12, compact: 16 },
};

/** For the admin page's picker, so the labels have one source of truth. */
export const AI_BADGE_SIZE_LABEL: Record<AiBadgeSize, string> = {
  48: "48px",
  32: "32px",
  28: "28px",
  24: "24px",
  20: "20px",
  16: "16px",
};

/**
 * The site-wide size from an `aiImagesConfig` story's content.
 *
 * Accepts the number and its string spelling: the admin page writes a string so the value
 * already matches what a Storyblok option field would store, should one be added to the
 * schema later. Anything else falls back to the default rather than throwing — a badge
 * that fails to appear is the one outcome this setting must never produce.
 */
export function readBadgeSize(
  content: Record<string, unknown> | null | undefined,
): AiBadgeSize {
  const raw = content?.[AI_BADGE_SIZE_FIELD];
  const value =
    typeof raw === "string" && /^\d+$/.test(raw.trim())
      ? Number(raw.trim())
      : raw;

  return isAiBadgeSize(value) ? value : DEFAULT_AI_BADGE_SIZE;
}
